import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { unitId, actionId } from '@/annotations'
import { AuthCard } from '@/components/AuthCard'
import { AuthCardBrand } from '@/components/AuthCardBrand'
import { AuthCardLead } from '@/components/AuthCardLead'
import { FieldStack } from '@/components/FieldStack'
import { Field } from '@/components/Field'
import { Button } from '@/components/Button'
import { Note } from '@/components/Note'
import { getApiClient, ApiProblemError, type Problem } from '@/data/apiClient'
import { AUTH_BRAND, A_FINISHREG_COPY } from '@/copy/registry'
import { TEXT_INPUT_CLASSNAME, TEXT_INPUT_STYLE } from '@/styles/formControls'
import { FormError, RateLimitedNotice, UnexpectedRefusal, isUnexpectedRefusal } from '@/screens/auth-shared/Refusals'

/** `a-finishreg` — the repair path for an authenticated identity with no organisation
 * (`identityRepairIdentity`, `401 no_identity` on any call — §3.1i, §3.1j routing table). "A
 * REPAIR state, never a step" — idempotent on the provider subject. */
export function AFinishreg({ onFinished }: { onFinished: () => void }) {
  const [orgName, setOrgName] = useState('')

  const repair = useMutation({
    mutationFn: async () => {
      const client = getApiClient()
      const { error } = await client.POST('/auth/repair', { body: { organisation_name: orgName } })
      if (error) throw new ApiProblemError(error as Problem)
    },
    onSuccess: onFinished,
  })

  const problem = repair.error instanceof ApiProblemError ? repair.error.problem : undefined

  return (
    <AuthCard unitId={unitId('a-finishreg')}>
      <AuthCardBrand label={AUTH_BRAND} />
      <h3 className="m-0 mb-3 text-section font-semibold">{A_FINISHREG_COPY.heading}</h3>
      <AuthCardLead>{A_FINISHREG_COPY.lead}</AuthCardLead>

      <FieldStack>
        <Field label={A_FINISHREG_COPY.orgNameLabel}>
          <input className={TEXT_INPUT_CLASSNAME} style={TEXT_INPUT_STYLE} value={orgName} onChange={(e) => setOrgName(e.target.value)} />
        </Field>
      </FieldStack>

      {problem && <AFinishregRefusal problem={problem} />}

      <Button data-act={actionId('identity.repair')} disabled={repair.isPending || !orgName} onClick={() => repair.mutate()}>
        {repair.isPending ? 'Finishing…' : A_FINISHREG_COPY.submitButton}
      </Button>

      <Note>{A_FINISHREG_COPY.repairNote}</Note>
      <Note>{A_FINISHREG_COPY.notAStepNote}</Note>
    </AuthCard>
  )
}

function AFinishregRefusal({ problem }: { problem: Problem }) {
  if (problem.refusal_kind === 'VALIDATION_FAILED') {
    return <FormError>{problem.errors?.map((e) => e.message).join(' ') ?? problem.title}</FormError>
  }
  if (problem.refusal_kind === 'RATE_LIMITED') return <RateLimitedNotice problem={problem} />
  return isUnexpectedRefusal(problem.refusal_kind) ? <UnexpectedRefusal problem={problem} /> : <FormError>{problem.title}</FormError>
}
